import { DOCUMENT, Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getQueryParam } from '../../core/initial-search-params';
import { SHARED_LINK_PARAM } from '../../core/shared-link';

@Injectable({
  providedIn: 'root',
})
export class NavigateService {
  private static readonly BACK_URL_PARAM = 'backUrl';
  private static readonly PLATFORM_URL_PARAM = 'bc';

  private readonly document = inject(DOCUMENT);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);

  /**
   * Whether the reader was opened with somewhere to go back to. The rest of the chain can only
   * infer a destination, so a caller whose single action has to land on the page the reader came
   * from — the payment block — asks this instead.
   */
  hasBackUrl(): boolean {
    return this.getValidBackUrl() !== null;
  }

  /**
   * Leaves the reader for wherever it came from, best source first:
   *
   *  1. backUrl — explicit, exact, and the only one the opener chose on purpose.
   *  2. the previous history entry — also exact, and the browser knows it even though it will
   *     not tell us what it is. Safe here because the reader is one route, so going back leaves
   *     it rather than moving inside it.
   *  3. the referrer — the right site, but cross-origin the browser sends only the origin, so
   *     the reader lands on the platform's home rather than on the page it was opened from.
   *  4. bc — the launching platform, which is branding rather than provenance: opened from the
   *     OPAC it holds the learning experience's OLE instance, a different site altogether.
   */
  goToPlatform(): void {
    const backUrl = this.getValidBackUrl();
    if (backUrl) {
      this.document.location.assign(backUrl);
      return;
    }

    if ((this.document.defaultView?.history.length ?? 0) > 1) {
      this.location.back();
      return;
    }

    const fallback =
      NavigateService.validateAbsoluteHttpUrl(this.document.referrer) ?? this.getValidPlatformUrl();
    if (fallback) {
      this.document.location.assign(fallback);
    }
  }

  private getValidBackUrl(): URL | null {
    const backUrl = NavigateService.validateAbsoluteHttpUrl(
      this.readParam(NavigateService.BACK_URL_PARAM),
    );
    if (backUrl) NavigateService.removeSharedLinkParam(backUrl);
    return backUrl;
  }

  /**
   * A reader opened from a shared-highlight link gets that same link as its backUrl, and the page
   * it names opens the reader again by itself: going back would bounce straight into the book.
   * The OTK backend matches the param case-insensitively, so drop it in any casing.
   */
  private static removeSharedLinkParam(url: URL): void {
    const sharedLinkParam = SHARED_LINK_PARAM.toLowerCase();
    [...url.searchParams.keys()]
      .filter((key) => key.toLowerCase() === sharedLinkParam)
      .forEach((key) => {
        url.searchParams.delete(key);
      });
  }

  private getValidPlatformUrl(): URL | null {
    return NavigateService.validateAbsoluteHttpUrl(
      this.readParam(NavigateService.PLATFORM_URL_PARAM),
    );
  }

  private readParam(name: string): string | null {
    return getQueryParam(this.route.snapshot, name);
  }

  private static validateAbsoluteHttpUrl(raw: string | null): URL | null {
    if (!raw) return null;

    try {
      const url = new URL(raw);
      // It arrives from a query string, so anything but http(s) could run instead of navigate.
      return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
    } catch {
      return null;
    }
  }
}
